import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import DisorderRegionSearch from './components/DisorderRegionSearch';
import BiomarkerSearch from './components/BiomarkerSearch';
import BiomarkerToDisorders from './components/BiomarkerToDisorders';
import './App.css';

function App() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    loadExcelData();
  }, []);

  const loadExcelData = async () => {
    try {
      const response = await fetch('/assets/Module1(Review Paper).xlsx');
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      const processedData = processExcelData(jsonData);
      setData(processedData);
      setLoading(false);
      
    } catch (error) {
      console.error('Error loading Excel file:', error);
      setLoading(false);
    }
  };

  const processExcelData = (excelData) => {
    const processed = [];
    let currentDisorder = '';

    for (let i = 1; i < excelData.length; i++) {
      const row = excelData[i];
      
      if (!row || row.length === 0 || (row.every(cell => !cell || cell === ''))) {
        continue;
      }

      const disorder = row[0]?.toString().trim();
      const region = row[1]?.toString().trim();
      const increase = row[2]?.toString().trim();
      const decrease = row[3]?.toString().trim();

      if (disorder && disorder !== '') {
        currentDisorder = disorder;
      }

      if (currentDisorder && (region || increase || decrease)) {
        const cleanRegion = region ? region.trim() : 'General';
        
        if (increase && increase !== '') {
          const increasedBiomarkers = parseBiomarkers(increase);
          increasedBiomarkers.forEach(biomarker => {
            if (biomarker && biomarker !== '') {
              processed.push({
                disorder: currentDisorder,
                region: cleanRegion,
                biomarker: biomarker.trim(),
                change: 'Increase'
              });
            }
          });
        }

        if (decrease && decrease !== '') {
          const decreasedBiomarkers = parseBiomarkers(decrease);
          decreasedBiomarkers.forEach(biomarker => {
            if (biomarker && biomarker !== '') {
              processed.push({
                disorder: currentDisorder,
                region: cleanRegion,
                biomarker: biomarker.trim(),
                change: 'Decrease'
              });
            }
          });
        }
      }
    }

    return processed;
  };

  const parseBiomarkers = (biomarkerString) => {
    if (!biomarkerString || biomarkerString === '') return [];
    
    let cleanedString = biomarkerString
      .replace(/Conn\.P/g, 'Conn.,P')
      .replace(/,,/g, ',')
      .replace(/,\s*,/g, ',')
      .trim();
    
    const biomarkers = [];
    let current = '';
    let parenCount = 0;
    
    for (let i = 0; i < cleanedString.length; i++) {
      const char = cleanedString[i];
      
      if (char === '(') {
        parenCount++;
        current += char;
      } else if (char === ')') {
        parenCount--;
        current += char;
      } else if (char === ',' && parenCount === 0) {
        if (current.trim()) {
          biomarkers.push(current.trim());
        }
        current = '';
      } else {
        current += char;
      }
    }
    
    if (current.trim()) {
      biomarkers.push(current.trim());
    }
    
    return biomarkers.filter(b => b && b !== '' && b !== ',');
  };

  const renderHomePage = () => (
    <div className="home-page">
      <div className="welcome-section">
        <h2>Welcome to Biomarker Analysis Tool</h2>
        <p>Choose your search method to explore neurological biomarkers</p>
      </div>
      
      <div className="action-buttons">
        <button 
          className="action-button disorder-button"
          onClick={() => setCurrentView('disorder')}
        >
          <div className="button-icon">🧠</div>
          <div className="button-content">
            <h3>Search by Disorder & Region</h3>
            <p>Find biomarkers for specific neurological disorders and brain regions</p>
          </div>
          <div className="button-arrow">→</div>
        </button>

        <button 
          className="action-button biomarker-button"
          onClick={() => setCurrentView('biomarker')}
        >
          <div className="button-icon">🔍</div>
          <div className="button-content">
            <h3>Search by Biomarker</h3>
            <p>Discover where specific biomarkers change across disorders and regions</p>
          </div>
          <div className="button-arrow">→</div>
        </button>

        {/* New button for Biomarker to Disorders Mapping */}
        <button 
          className="action-button mapping-button"
          onClick={() => setCurrentView('mapping')}
        >
          <div className="button-icon">🗺️</div>
          <div className="button-content">
            <h3>Biomarker to Disorders Mapping</h3>
            <p>Find disorders that contain specific biomarker combinations with change types</p>
          </div>
          <div className="button-arrow">→</div>
        </button>
      </div>
    </div>
  );

  const renderBackButton = () => (
    <button 
      className="back-button"
      onClick={() => setCurrentView('home')}
    >
      ← Back to Home
    </button>
  );

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Biomarker Analysis Tool</h1>
        <p className="app-subtitle">Explore biomarkers across different disorders and brain regions</p>
        {currentView !== 'home' && renderBackButton()}
      </header>
      
      <main>
        {currentView === 'home' && renderHomePage()}
        {currentView === 'disorder' && <DisorderRegionSearch data={data} />}
        {currentView === 'biomarker' && <BiomarkerSearch data={data} />}
        {currentView === 'mapping' && <BiomarkerToDisorders data={data} />}
      </main>
    </div>
  );
}

export default App;